// FieldVision Admin - Main App Component v5
// Tilfojet: User management routes (/users, /users/:id)

import React, { useState, useEffect } from 'react';
import { isAuthenticated } from './services/api';
import AdminLogin from './components/AdminLogin';
import AdminHeader from './components/AdminHeader';
import OrderList from './components/OrderList';
import OrderDetail from './components/OrderDetail';
import UserList from './components/UserList';
import UserDetail from './components/UserDetail';
import PilotList from './components/PilotList';
import ConfirmPage from './components/ConfirmPage';

type View = 'orders' | 'users' | 'pilots';

// Parse URL for confirm token
function getConfirmToken(): string | null {
  const path = window.location.pathname;
  const match = path.match(/^\/confirm\/([a-f0-9-]+)$/i);
  return match ? match[1] : null;
}

const App: React.FC = () => {
  const [loggedIn, setLoggedIn] = useState(isAuthenticated());
  const [currentView, setCurrentView] = useState<View>('orders');
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [confirmToken, setConfirmToken] = useState<string | null>(getConfirmToken());

  // Check auth og URL on mount
  useEffect(() => {
    setLoggedIn(isAuthenticated());
    setConfirmToken(getConfirmToken());

    // Lyt efter URL aendringer (browser back/forward)
    const handlePopState = () => {
      setConfirmToken(getConfirmToken());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleLoginSuccess = () => {
    setLoggedIn(true);
  };

  const handleSelectOrder = (orderId: number) => {
    setSelectedOrderId(orderId);
  };

  const handleBackToOrderList = () => {
    setSelectedOrderId(null);
  };

  const handleSelectUser = (userId: number) => {
    setSelectedUserId(userId);
  };

  const handleBackToUserList = () => {
    setSelectedUserId(null);
  };

  const handleNavigate = (view: View) => {
    setCurrentView(view);
    setSelectedOrderId(null);
    setSelectedUserId(null);
  };

  // Hvis vi er paa confirm page, vis den (ingen auth kraevet)
  if (confirmToken) {
    return <ConfirmPage token={confirmToken} />;
  }

  // Not logged in - show login
  if (!loggedIn) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  // Logged in - show admin interface
  return (
    <div style={styles.app}>
      <AdminHeader currentView={currentView} onNavigate={handleNavigate} />
      <main style={styles.main}>
        {/* Orders view */}
        {currentView === 'orders' && !selectedOrderId && (
          <OrderList onSelectOrder={handleSelectOrder} />
        )}
        {currentView === 'orders' && selectedOrderId && (
          <OrderDetail orderId={selectedOrderId} onBack={handleBackToOrderList} />
        )}

        {/* Users view */}
        {currentView === 'users' && !selectedUserId && (
          <UserList onSelectUser={handleSelectUser} />
        )}
        {currentView === 'users' && selectedUserId && (
          <UserDetail userId={selectedUserId} onBack={handleBackToUserList} />
        )}

        {/* Pilots view */}
        {currentView === 'pilots' && <PilotList />}
      </main>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  app: {
    minHeight: '100vh',
    backgroundColor: '#f3f4f6',
  },
  main: {
    minHeight: 'calc(100vh - 60px)',
  },
};

export default App;
